'use client';

import {
    RadioField,
    SelectField,
    StockStatusLabel,
    TextPassage,
} from '@petsmart-ui/sparky';
import type { ChangeEvent, FormEvent } from 'react';
import { useState, useEffect } from 'react';
import type { StockStatusEnum } from '@petsmart-ui/sparky';

import type { ShipToMeProps } from '@app/pdp/components/AddToCart/DeliveryMethods/types';
import { getAvailableQuantities } from '@app/pdp/components/AddToCart/DeliveryMethods/utils';
import AddToCartBtn from '@app/pdp/components/AddToCart/common/AddToCartBtn';
import { useAddToCTCart } from '@app/pdp/components/AddToCart/hooks/useAddToCTCart';
import { EstimatedTreatPoints } from '@app/pdp/components/EstimatedTreatPoints';
import PersonalizedProduct from '@app/pdp/components/PersonalizedProduct/PersonalizedProduct';
import { usePersonalizedProduct } from '@app/pdp/components/PersonalizedProduct/hooks';

import UnavailableDeliveryMethod from '@app/pdp/components/AddToCart/DeliveryMethods/UnavailableDeliveryMethod';
import MinimumQuantityLabel from '@app/pdp/components/AddToCart/common/MinimumQuantityLabel';
import { DELIVERY_METHODS } from '@app/pdp/constants';
import { useProductDetails } from '@app/pdp/hooks';
import { clsx, useOptimizely, usePageLayout } from '@libs/common-next';
import { LOCALIZED, DeliveryType, getCountry } from '@libs/core';
import { hasStockType } from '@libs/shared-ui';
import './ShipToMe.scss';

export default function ShipToMe({
    product,
    inventory,
    selectVariant,
    handleAddToCart,
    value,
    stockStatus,
    minimumCaseQty,
    inventoryQuantity,
    isAvailable = true,
    isPageLoading,
}: Readonly<ShipToMeProps>) {
    const [selectedQty, setSelectedQty] = useState(minimumCaseQty || 1);
    const { ocapi, setOcapi, setErrorAlert, setRefreshNeededError } =
        usePageLayout();
    const { selectedProduct } = useProductDetails();
    const { addToCTCart, isLoading: isCTLoading } = useAddToCTCart();
    const { isPersonalized } = usePersonalizedProduct();

    const [addedToCart, setAddedToCart] = useState(false);
    const [isLoading, setIsLoading] = useState(false);

    const country = getCountry();

    const onBasket = ocapi.data?.basket?.product_items?.find(
        productItem =>
            Number(productItem.product_id) === product.id &&
            productItem.inventory_id === inventory.id
    );

    const onBasketQty = onBasket?.quantity ?? 0;

    const onHand = inventory.ats - onBasketQty;
    // TODO: review the usage of inStock as this is the only delivery method where we check qty against added to cart products.
    const inStock = onHand > 0 && inventory.orderable;

    const disableAddToCart = !hasStockType(stockStatus) || !inStock;

    const availableQuantities = getAvailableQuantities(
        minimumCaseQty,
        inventoryQuantity,
        selectedProduct.maxOrderQty
    );

    useEffect(() => {
        setIsLoading(isCTLoading);
    }, [isCTLoading]);

    const onQuantityChange = (event: ChangeEvent<HTMLSelectElement>) => {
        const qty = event?.target.value;

        if (qty) {
            setOcapi(oldOcapi => {
                if (oldOcapi.data) {
                    return {
                        ...oldOcapi,
                        basket: {
                            ...oldOcapi.data,
                            order_total: Number(qty),
                        },
                    };
                }
                return oldOcapi;
            });
            setSelectedQty(parseInt(qty, 10));
        }
    };

    const inventoryListId = LOCALIZED.webInventory;
    const productId = selectVariant?.id?.toString() || product.objectID;

    const shouldDisplayTreatPoints = hasStockType(stockStatus);
    const userContext = useOptimizely().context; // Optimizely

    const onSubmit = async (e: FormEvent<HTMLFormElement>) => {
        e.preventDefault();

        if (!isAvailable || isPageLoading) {
            return;
        }

        setIsLoading(true);

        // We don't care if the previous request failed.
        // Continue with add to cart.
        try {
            await addToCTCart({
                country,
                sku: productId,
                quantity: selectedQty,
                fulfillmentMethod: DeliveryType.STH,
                storeId: inventoryListId,
            });
            setAddedToCart(true);

            userContext?.trackEvent('sth_atc__ot_'); // Optimizely
        } catch {
            setRefreshNeededError(true);
            setErrorAlert(true);
        } finally {
            setIsLoading(false);
        }
    };

    if (!isAvailable && !isPageLoading) {
        return <UnavailableDeliveryMethod deliveryMethod="Ship to Me" />;
    }

    return (
        <RadioField.Item
            className="ship-to-home"
            data-testid="stm-radio-button"
            value={value}
            text={
                <span className="radio-field-title sparky-u-theme-typography-body-lg-bold">
                    Ship to me
                </span>
            }
            itemAfter={
                <div data-testid="stock-status" className="stock-status">
                    {stockStatus !== 'Pending' ? (
                        <StockStatusLabel
                            labelFewLeftInStock="Only a few left"
                            stockStatus={stockStatus as StockStatusEnum}
                        />
                    ) : null}
                </div>
            }
            hiddenContent={
                <>
                    <form onSubmit={onSubmit} data-testid="form-ship">
                        {product.isPoBoxRestricted && (
                            <TextPassage className="additional-message">
                                This item does not ship to a PO box and may
                                exceed the standard delivery window
                            </TextPassage>
                        )}
                        <MinimumQuantityLabel minimumCaseQty={minimumCaseQty} />
                        {!disableAddToCart && (
                            <>
                                <div className="qty-selector-wrapper">
                                    <SelectField
                                        data-testid="select-quantity"
                                        value={selectedQty}
                                        onChange={onQuantityChange}
                                        label="Quantity"
                                        items={availableQuantities}
                                        disabled={
                                            availableQuantities.length < 1
                                        }
                                        defaultValue={selectedQty}
                                    />
                                </div>
                                {shouldDisplayTreatPoints && (
                                    <EstimatedTreatPoints qty={selectedQty} />
                                )}
                                <br />
                            </>
                        )}

                        {!isPersonalized && (
                            <AddToCartBtn
                                text={
                                    addedToCart
                                        ? DELIVERY_METHODS.ADDED_TO_CART
                                        : DELIVERY_METHODS.ADD_TO_CART
                                }
                                className={clsx(
                                    addedToCart &&
                                        'add-to-cart-button add-to-cart-button__green'
                                )}
                                setAddedToCart={setAddedToCart}
                                addedToCart={addedToCart}
                                disabled={disableAddToCart}
                                isLoading={isLoading || isPageLoading}
                            />
                        )}
                    </form>
                    {isPersonalized && (
                        <PersonalizedProduct
                            quantity={selectedQty}
                            addToCart={handleAddToCart}
                            disableAddToCart={disableAddToCart}
                        />
                    )}
                </>
            }
        />
    );
}
